/**
 * Surf presenter remote — ephemeral signal model shared by phone (remote) and display.
 * The channel is AppSync NONE pub/sub (no persistence). See docs/surf-presenter-remote-plan.md.
 */
export const PRESENT_ACTIONS = [
    "goto",
    "blank",
    "unblank",
    "pointer",
    "ping",
    "end",
];
/** Minimum sessionId length enforced server-side (channel secrecy). */
export const PRESENT_SESSION_MIN_LEN = 24;
export const PRESENT_SESSION_MAX_LEN = 128;
const NONCE_ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
/**
 * Generate an opaque crypto-random session nonce (≥24 chars, ~128 bit entropy).
 * Not derived from deckId: knowing the deck does not reveal the channel.
 */
export function newSessionId(length = 32) {
    const len = Math.max(PRESENT_SESSION_MIN_LEN, Math.min(PRESENT_SESSION_MAX_LEN, length));
    const cryptoObj = typeof globalThis !== "undefined"
        ? globalThis.crypto
        : undefined;
    if (cryptoObj?.getRandomValues) {
        const bytes = new Uint8Array(len);
        cryptoObj.getRandomValues(bytes);
        let out = "";
        for (const b of bytes)
            out += NONCE_ALPHABET[b % NONCE_ALPHABET.length];
        return out;
    }
    // Fallback (non-crypto) — only reached in environments without WebCrypto.
    let out = "";
    for (let i = 0; i < len; i += 1) {
        out += NONCE_ALPHABET[Math.floor(Math.random() * NONCE_ALPHABET.length)];
    }
    return out;
}
export function isValidSessionId(sessionId) {
    return sessionId.length >= PRESENT_SESSION_MIN_LEN && sessionId.length <= PRESENT_SESSION_MAX_LEN;
}
export function isPresentAction(value) {
    return typeof value === "string" && PRESENT_ACTIONS.includes(value);
}
/** Clamp a normalized pointer coordinate to [0,1] or null. */
export function clampPointer(value) {
    if (value == null || typeof value !== "number" || Number.isNaN(value))
        return null;
    return Math.max(0, Math.min(1, value));
}
/** Normalize a signal received off the wire into a strongly-typed value (defensive). */
export function normalizePresentSignal(raw) {
    if (!raw || typeof raw !== "object")
        return null;
    const obj = raw;
    if (!isPresentAction(obj.action))
        return null;
    const sessionId = `${obj.sessionId ?? ""}`;
    if (!isValidSessionId(sessionId))
        return null;
    const slideIndexRaw = obj.slideIndex;
    const slideIndex = typeof slideIndexRaw === "number" && !Number.isNaN(slideIndexRaw)
        ? Math.max(0, Math.floor(slideIndexRaw))
        : null;
    const tsRaw = obj.ts;
    // Server stamps `ts` (AWSTimestamp, ms). If it's missing/0 (e.g. schema not yet
    // deployed or an old payload), fall back to now so the display's anti-replay
    // guard (`ts <= last`) does not drop every event as "stale".
    const tsParsed = typeof tsRaw === "number" && !Number.isNaN(tsRaw) ? tsRaw : 0;
    const ts = tsParsed > 0 ? tsParsed : Date.now();
    return {
        sessionId,
        deckId: `${obj.deckId ?? ""}`,
        owner: `${obj.owner ?? ""}`,
        action: obj.action,
        slideIndex,
        pointerX: clampPointer(obj.pointerX),
        pointerY: clampPointer(obj.pointerY),
        senderRole: obj.senderRole === "display" ? "display" : "remote",
        ts,
    };
}
export const PRESENT_GRAPHQL = {
    sendPresentSignal: /* GraphQL */ `
    mutation SurfSendPresentSignal($input: PresentSignalInput!) {
      sendPresentSignal(input: $input) {
        sessionId
        deckId
        owner
        action
        slideIndex
        pointerX
        pointerY
        senderRole
        ts
      }
    }
  `,
    onPresentSignal: /* GraphQL */ `
    subscription SurfOnPresentSignal($sessionId: ID!) {
      onPresentSignal(sessionId: $sessionId) {
        sessionId
        deckId
        owner
        action
        slideIndex
        pointerX
        pointerY
        senderRole
        ts
      }
    }
  `,
};
